import {
  MenuItem,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import {
  Children,
  isValidElement,
  ReactElement,
  ReactNode,
  useId,
  useState,
} from 'react';
import {
  LelyFieldHelperText,
  LelyFieldSharedProps,
  LelyFieldUnitAdornment,
  shouldShowLelyFieldUnitText,
} from '../common/lelyFieldShared';

export type LelySelectProps = LelyFieldSharedProps & {
  label?: ReactNode;
  disabled?: boolean;
  placeholder?: string;
  value?: TextFieldProps['value'];
  onChange?: TextFieldProps['onChange'];
  children?: ReactNode;
  sx?: TextFieldProps['sx'];
  id?: string;
  name?: string;
  'data-testid'?: string;
  fullWidth?: boolean;
};

function isSelectValueEmpty(value: unknown) {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

function getSelectedOptionLabel(
  children: ReactNode,
  selected: unknown,
): ReactNode {
  const matched = Children.toArray(children).find((child) => {
    if (!isValidElement<{ value?: unknown; children?: ReactNode }>(child)) {
      return false;
    }

    return child.props.value === selected;
  }) as ReactElement<{ value?: unknown; children?: ReactNode }> | undefined;

  return matched?.props.children ?? (selected as ReactNode);
}

function LelySelect({
  label,
  showHelperText,
  disabled,
  helperText,
  valid = true,
  placeholder,
  showUnitText,
  unitText,
  value,
  onChange,
  children,
  sx,
  id,
  name,
  'data-testid': dataTestId,
  fullWidth = true,
}: LelySelectProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  // Native <input> inside MUI Select needs name/id for a11y form-field checks
  const fieldName = name ?? fieldId;
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const hasLabel = label != null && label !== '';
  const isEmpty = isSelectValueEmpty(value);
  const shrinkLabel = !isEmpty || open || focused;
  const shouldShowUnitText = shouldShowLelyFieldUnitText(
    showUnitText,
    unitText,
  );

  return (
    <Stack sx={sx}>
      <TextField
        select
        variant="filled"
        id={fieldId}
        name={fieldName}
        data-testid={dataTestId}
        label={hasLabel ? label : undefined}
        hiddenLabel={!hasLabel}
        value={value ?? ''}
        onChange={onChange}
        disabled={disabled}
        error={!valid}
        fullWidth={fullWidth}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        InputLabelProps={{
          shrink: shrinkLabel,
        }}
        SelectProps={{
          displayEmpty: true,
          open,
          onOpen: () => setOpen(true),
          onClose: () => setOpen(false),
          renderValue: (selected) => {
            if (isSelectValueEmpty(selected)) {
              if (!shrinkLabel || !placeholder) {
                return '';
              }

              return (
                <Typography component="span" variant="xs" color="text.subtle">
                  {placeholder}
                </Typography>
              );
            }

            return getSelectedOptionLabel(children, selected);
          },
        }}
        InputProps={{
          endAdornment: shouldShowUnitText ? (
            <LelyFieldUnitAdornment unitText={unitText} />
          ) : undefined,
        }}
      >
        {placeholder && (
          <MenuItem value="" disabled sx={{ display: 'none' }}>
            {placeholder}
          </MenuItem>
        )}
        {children}
      </TextField>
      <LelyFieldHelperText
        showHelperText={showHelperText}
        helperText={helperText}
        valid={valid}
      />
    </Stack>
  );
}

export default LelySelect;
